/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/label-has-for, react/require-default-props, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions  */

import React from 'react';
import { string, oneOfType, bool, number, func, node } from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import classNames from 'classnames';
import { isNil, isEmpty, get, trim, noop } from 'lodash';
import { formatCurrency } from 'utils/string';
import Popover from '../Popover';
import s from './Input.css';
import { ON, OFF } from './constant';

const validateInputNumber = (isNumber, value, preValue) =>
  isNumber && value !== preValue && !isNaN(value);

const getClassName = (orginalClass, additionalClass, conditional) =>
  classNames(orginalClass, { [additionalClass]: !isNil(conditional) });

const errorIcon = 'error_outline';
const successIcon = 'check';
class Input extends React.PureComponent {
  /**
   * Input propTypes
   */
  static propTypes = {
    id: string,
    type: string,
    name: string,
    value: oneOfType([string, number]),
    label: string,
    disabled: bool,
    readOnly: bool,
    icon: string,
    onChange: func,
    onBlur: func,
    errorMsg: string,
    touched: bool,
    placeholder: string,
    onMouseDown: func,
    maxLength: number,
    autoFocus: bool,
    require: bool,
    className: string,
    autoComplete: bool,
    iconElement: node,
    popoverPosition: string,
    isNumber: bool,
    numberFormat: string,
    preFormat: func,
    touch: func,
  };

  static defaultProps = {
    id: '',
    errorMsg: '',
    className: '',
    touched: false,
    type: 'text',
    maxLength: null,
    autoFocus: false,
    require: false,
    autoComplete: true,
    iconElement: undefined,
    popoverPosition: 'right',
    isNumber: false,
    numberFormat: '',
    preFormat: noop,
    touch: noop,
  };

  constructor(props) {
    super(props);

    this.state = { isFocus: false, value: props.value };
  }

  componentDidMount() {
    this.props.autoFocus && this.input.focus();
  }

  static getDerivedStateFromProps(props, state) {
    const inputNumberTypeValid = validateInputNumber(
      props.isNumber,
      props.value,
      state.value,
    );
    if (!props.isNumber || !props.value || inputNumberTypeValid) {
      return {
        ...state,
        value: props.value,
      };
    }

    return state;
  }

  get renderErrorIcon() {
    const { touched, errorMsg, value, popoverPosition } = this.props;

    // Not render icon for 2 following case:
    // 1. Input not touched
    // 2. Touch but not validate & value is empty
    // 3. Input is focus
    if (!touched || (isEmpty(value) && errorMsg === '') || this.state.isFocus)
      return null;

    return errorMsg !== '' ? (
      <Popover
        placement={popoverPosition}
        className={s.iconError}
        component={<i className="material-icons">{errorIcon}</i>}
      >
        <span className={s.error}>{errorMsg}</span>
      </Popover>
    ) : (
      <span className={s.iconSuccess} role="button">
        <i className="material-icons">{successIcon}</i>
      </span>
    );
  }

  setInput = ref => {
    this.input = ref;
  };

  /**
   * Handle event onChange
   *
   * @param  {SyntheticEvent} event   The Synthetic event
   * @returns {Void}
   */
  handleOnChange = event => {
    const { maxLength, onChange, numberFormat, preFormat } = this.props;
    const value = get(event, 'target.value') || '';
    const trimValue = trim(value);

    // Don't fire change event for all space characters
    if (value !== trimValue && isEmpty(trimValue)) {
      return;
    }
    if (numberFormat && isNaN(preFormat(trimValue))) return;
    // Don't have maxLength, allow to input all
    if (isNil(maxLength)) {
      onChange && onChange(event);
    } else {
      value.length <= maxLength && onChange && onChange(event, null, trimValue);
    }
  };

  /**
   * Handle event onBlur
   *
   * @param  {SyntheticEvent} event   The Synthetic event
   * @returns {Void}
   */
  handleOnBlur = event => {
    this.setState({ isFocus: false });
    this.props.onBlur && this.props.onBlur(event);
  };

  /**
   * Handle event onFocus, update state isFocus = true
   *
   * @param  {SyntheticEvent} event   The Synthetic event
   * @returns {Void}
   */
  handleOnFocus = () => {
    const { touch } = this.props;
    this.setState({ isFocus: true }, touch);
  };

  /**
   * Handle event onMouseDown
   *
   * @param  {SyntheticEvent} event   The Synthetic event
   * @returns {Void}
   */
  handleMouseDown = event => {
    this.props.onMouseDown && this.props.onMouseDown(event);
  };

  render() {
    const {
      placeholder,
      name,
      type,
      disabled,
      readOnly,
      icon,
      label,
      id,
      errorMsg,
      touched,
      maxLength,
      className,
      require,
      autoComplete,
      iconElement,
      numberFormat,
      preFormat,
    } = this.props;
    const { value } = this.state;
    const hasValue = !isEmpty(placeholder) || (value && !isEmpty(`${value}`));
    const hasError = !isEmpty(errorMsg) && touched;
    const wrapperClassName = classNames(className, s.wrapper, {
      [s.hasValue]: hasValue,
      [s.hasError]: hasError,
    });
    const iconClassName = classNames('material-icons', s.icon);
    const inputClassName = getClassName(s.input, s.inputIcon, icon);
    const labelClassName = getClassName(s.label, s.inputIcon, icon);
    const inputProps = type === 'number' ? { pattern: '[0-9]*', min: 0 } : {};
    const iconComponent = iconElement || (
      <i className={iconClassName}>{icon}</i>
    );

    const v =
      numberFormat && value ? formatCurrency(preFormat(value), false) : value;

    return (
      <div className={wrapperClassName}>
        {iconComponent}
        <input
          ref={this.setInput}
          placeholder={placeholder}
          type={type}
          name={name}
          className={inputClassName}
          value={v}
          disabled={disabled}
          readOnly={readOnly}
          onChange={this.handleOnChange}
          onBlur={this.handleOnBlur}
          onFocus={this.handleOnFocus}
          onMouseDown={this.handleMouseDown}
          maxLength={maxLength}
          autoComplete={autoComplete ? ON : OFF}
          {...inputProps}
        />
        {this.renderErrorIcon}
        {label && (
          <label className={labelClassName} htmlFor={id}>
            {label}
            {require && <sup>(*)</sup>}
          </label>
        )}
        <div className={s.underline} />
      </div>
    );
  }
}

export default withStyles(s)(Input);
